import React, { useEffect, useState } from 'react';
import { AlertCircle, Receipt } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { listBooks } from '../../../../../services/BooksService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { BillingClient, listBillingClients } from '../../../../../services/InvoicesService';
import { JournalLine, postJournalEntry } from '../../../../../services/JournalEntriesService';
import {
  SalesReceipt,
  formatEuro
} from '../masters/financeMasters';
import {
  DocumentLine,
  DocumentLinesEditor,
  FormSkeleton,
  JournalPreview,
  LineProduct,
  SearchSelect,
  linesSubtotal,
  linesVat,
  newLine,
  today
} from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const TARGET_ACCOUNTS = [
  { code: '570000', name: 'Caja, euros' },
  { code: '572000', name: 'Bancos c/c' }
];

interface SalesReceiptFormProps {
  privacyMode: boolean;
  receipt?: SalesReceipt;
  nextNumber: string;
  onSave: (receipt: SalesReceipt) => void;
  onClose: () => void;
}

export const SalesReceiptForm = ({ privacyMode, receipt, nextNumber, onSave, onClose }: SalesReceiptFormProps) => {
  const { notify } = useToast();

  const { methods } = usePaymentMethods();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clients, setClients] = useState<BillingClient[]>([]);
  const [products, setProducts] = useState<LineProduct[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const number = receipt ? receipt.number : nextNumber;
  const [clientId, setClientId] = useState(receipt ? receipt.clientId : '');
  const [date, setDate] = useState(receipt ? receipt.date : today());
  const [lines, setLines] = useState<DocumentLine[]>(receipt && receipt.lines.length > 0 ? receipt.lines : [newLine()]);
  const [paymentMethod, setPaymentMethod] = useState(receipt ? receipt.paymentMethod : 'Efectivo (Caja)');
  const [targetAccount, setTargetAccount] = useState(receipt ? receipt.targetAccount : '570000');

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [clientList, books, chart] = await Promise.all([listBillingClients(), listBooks(), listChartOfAccounts()]);
        if (cancelled) return;
        setClients(clientList);
        setProducts(books.map(b => ({ id: b.id, name: b.title, sku: b.isbn || b.sku, price: b.price, vat: 4 })));
        setAccounts(chart);
        setLoadError('');
      } catch (err) {
        console.error('Error loading sales receipt form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los datos del recibo.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // La forma de pago propone la cuenta destino del maestro (efectivo a caja, el resto a banco)
  const handleMethodChange = (name: string) => {
    setPaymentMethod(name);
    const method = methods.find(m => m.name === name);
    if (method) setTargetAccount(method.account === '570000' ? '570000' : '572000');
  };

  const client = clients.find(c => c.id === clientId);
  const subtotal = linesSubtotal(lines);
  const vatTotal = linesVat(lines);
  const total = subtotal + vatTotal;
  const target = TARGET_ACCOUNTS.find(a => a.code === targetAccount) || TARGET_ACCOUNTS[0];

  const journalLines: JournalLine[] = [
    { account: target.code, accountName: target.name, debit: total, credit: 0 },
    { account: '700000', accountName: 'Ventas de mercaderías', debit: 0, credit: subtotal },
    { account: '477000', accountName: 'H.P. IVA Repercutido', debit: 0, credit: vatTotal }
  ];

  const validate = () => {
    if (!client) return 'Seleccione un cliente.';
    if (!date) return 'La fecha del recibo es obligatoria.';
    if (lines.some(l => !l.description.trim())) return 'Todas las líneas necesitan una descripción.';
    if (total <= 0) return 'El importe total del recibo debe ser mayor que cero.';
    return '';
  };

  const buildReceipt = (status: SalesReceipt['status']): SalesReceipt => ({
    number,
    clientId,
    clientName: client.name,
    clientCif: client.cif,
    date,
    lines,
    paymentMethod,
    targetAccount,
    subtotal,
    vat: vatTotal,
    total,
    status
  });

  const handleSave = () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    onSave(buildReceipt('Borrador'));
    notify('Recibo guardado', `Recibo ${number} guardado como borrador.`, 'info');
    onClose();
  };

  // Primero el asiento: el recibo solo pasa a emitido si el asiento se ha guardado
  const handleIssue = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      await postJournalEntry(`Recibo de venta ${number} a ${client.name}`, journalLines, 'Recibos de Venta', accounts, date);
    } catch (err) {
      console.error('Error posting sales receipt journal entry:', err);
      setFormError(`No se pudo contabilizar el recibo: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving(false);
      return;
    }
    onSave(buildReceipt('Emitido'));
    notify('Recibo emitido', `Recibo ${number} emitido y contabilizado en el Libro Diario [${target.code.slice(0, 3)} a 700 + 477].`, 'info');
    setSaving(false);
    onClose();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const clientOptions = clients.map(c => ({ value: c.id, label: c.name, sub: c.cif ? `CIF: ${c.cif}` : undefined }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">VENTAS Y COBROS • RECIBOS DE VENTA</span>
          <h3 className="fin-doc__title"><Receipt size={14} /> Recibo de venta (contado)</h3>
        </div>
        <span className="fin-doc__badge">{number}</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cliente *</label>
          <SearchSelect
            options={clientOptions}
            value={clientId}
            onChange={setClientId}
            placeholder={receipt && !client ? `${receipt.clientName} (elegir cliente)` : 'Buscar cliente por nombre o CIF...'}
            emptyText="No hay clientes"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Forma de pago *</label>
          <select className="sidebar-form-input" value={paymentMethod} onChange={(e) => handleMethodChange(e.target.value)}>
            {methods.filter(m => m.isActive || m.name === paymentMethod).map(m => (
              <option key={m.id} value={m.name}>{m.name}</option>
            ))}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta destino *</label>
          <select className="sidebar-form-input" value={targetAccount} onChange={(e) => setTargetAccount(e.target.value)}>
            {TARGET_ACCOUNTS.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
          </select>
        </div>
      </div>

      <DocumentLinesEditor lines={lines} onChange={setLines} products={products} privacyMode={privacyMode} />

      <div className="fin-doc__totals">
        <span>Subtotal: <strong>{formatEuro(subtotal, privacyMode)}</strong></span>
        <span>IVA: <strong>{formatEuro(vatTotal, privacyMode)}</strong></span>
        <span className="fin-doc__total">Total recibo: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <JournalPreview lines={journalLines} privacyMode={privacyMode} />

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" disabled={saving} onClick={handleSave}>
          Guardar
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={saving} onClick={handleIssue}>
          {saving ? 'Contabilizando...' : 'Emitir y contabilizar'}
        </button>
      </div>
    </div>
  );
};

export default SalesReceiptForm;
